/**
 * The ledger's own line items (server/api-util/ledger/codes.js), as the
 * order breakdown shows them: everything an order's money did after
 * checkout — refunds, fees returned, labels, carrier adjustments, a bank
 * dispute. In display order. Each has a label per side under
 * OrderBreakdown.ledger.<name>.<customer|provider>.
 */
export const LEDGER_CODES = [
  'line-item/refund-items',
  'line-item/refund-shipping',
  'line-item/refund-tax',
  'line-item/goodwill-refund',
  'line-item/restocking-fee',
  'line-item/return-label-buyer',
  'line-item/refund-commission',
  'line-item/refund-card-fee',
  'line-item/shipping-label-deduction',
  'line-item/shipping-overage',
  'line-item/return-label-seller',
  'line-item/return-label-overage',
  'line-item/chargeback',
  'line-item/chargeback-seller',
  'line-item/chargeback-fee',
  'line-item/carried-to-balance',
];

export const isLedgerCode = code => LEDGER_CODES.includes(code);

/** The label id of a ledger line for a side. */
export const ledgerLabelId = (code, role) => `OrderBreakdown.ledger.${code.replace('line-item/', '')}.${role}`;

/**
 * The ledger lines for a side, one row per code (an order can carry several
 * of a code, one per return or label), in display order.
 * @returns {Array<{ code, amount: Money-like { amount, currency } }>}
 */
export const ledgerRows = (lineItems, role) =>
  LEDGER_CODES.map(code => {
    const lines = (lineItems || []).filter(line => line.code === code && !line.reversal && (line.includeFor || []).includes(role));
    if (!lines.length) return null;
    const amount = lines.reduce((sum, line) => sum + (line.lineTotal?.amount ?? 0), 0);
    return amount === 0 ? null : { code, amount, currency: lines[0].lineTotal?.currency };
  }).filter(Boolean);

/**
 * The seller's labels whose void the carrier hasn't approved, as notes under the breakdown's
 * label rows: a void still waiting (the label's cost comes back once the carrier approves it)
 * or one the carrier refused (it stays charged). Only labels charged to the seller's earnings:
 * a return label they paid for, an outbound label bought from their earnings.
 * @returns {Array<{ code, status: 'requested'|'rejected', cents }>}
 */
export const labelVoidNotes = voidedLabels =>
  (Array.isArray(voidedLabels) ? voidedLabels : [])
    .filter(label => label?.status === 'requested' || label?.status === 'rejected')
    .map(label =>
      label.kind === 'return'
        ? label.paidBy === 'seller' && label.costCents > 0
          ? { code: 'line-item/return-label-seller', status: label.status, cents: label.costCents }
          : null
        : label.deductionCents > 0
        ? { code: 'line-item/shipping-label-deduction', status: label.status, cents: label.deductionCents }
        : null
    )
    .filter(Boolean);
